import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ResultsSummaryTableData, ReviewProjectsTableData } from "@app/shared/provider-change-wizards/interfaces";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ChangeEvent, JSX, useContext, useEffect, useMemo, useState } from "react";
import { BulkTargetingWizardContext } from "./TargetingWizardContext";
import { BulkTargetExistingTargetActionEnum, BulkTargetingWizardStepProps } from "./interfaces";
import Features from "@app/feature-flags/features";
import PageSection from "@app/shared/PageSection";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESCard } from "@app/shared/qes-standards/Card";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import ReportCheckbox from "@app/targeting/import/components/ReportCheckbox";
import { getTargetingReportSections } from "@app/targeting/import/reportSections";

const TargetingWizardConfigureReport = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const wizardContext = useContext(BulkTargetingWizardContext);
  const userRoles = useContext(UserContext).roles;
  const [targetingReasons, setTargetingReason] = useState<JSX.Element[]>([]);
  const isDistinctServicingProviderReportEnabled = featureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT) && userRoles.includes(Roles.DISTINCT_SERVICING_USER);
  const isReportMetadataTargetTermEnabled = featureFlags.has(Features.REPORT_METADATA_TARGET_TERM);
  const isVolumeTargetingReportConfigurationEnabled = featureFlags.has(Features.VOLUME_TARGETING_REPORT_CONFIGURATION);

  const reportSections = useMemo(
    () => getTargetingReportSections(wizardContext.isDistinctServicingProviderReport, wizardContext.projectType),
    [wizardContext.isDistinctServicingProviderReport, wizardContext.projectType]
  );

  const reviewColumnConfig: ColumnConfig = {
    details: {
      displayText: "",
      noFilter: true,
      noSort: true,
      testId: "details",
    },
    projectName: {
      displayText: `Projects (${wizardContext.reviewProjects.length})`,
      testId: "project-name",
    },
    validItems: {
      displayText: "Results Summary",
      noFilter: true,
      noSort: true,
      testId: "results-summary",
    },
  };

  useEffect(() => {
    wizardContext.projectionProviderService.getProviderTargetingReasons()
    .then((response) => setTargetingReason(response.map((r, i) => <option key={i} value={r}>{r}</option>)));
  }, []);

  const handleDistinctProviderReportChange = (isDistinct: boolean) => {
    wizardContext.setIsDistinctServicingProviderReport(isDistinct);
  };

  useEffect(() => {
    const hasRequiredFields = !!wizardContext.targetingReason &&
      (wizardContext.targetingReason !== "Other" || !!wizardContext.comment) &&
      !!wizardContext.formatType &&
      !!wizardContext.filename &&
      !!wizardContext.existingTargetAction;
    const hasReports = !isVolumeTargetingReportConfigurationEnabled || (wizardContext.selectedReports?.length > 0);
    props.stepIsValid(hasRequiredFields && hasReports);
  }, [wizardContext.targetingReason, wizardContext.comment, wizardContext.formatType, wizardContext.filename, wizardContext.existingTargetAction, wizardContext.selectedReports, isVolumeTargetingReportConfigurationEnabled]);

  return (
    <PageSection>
      <div className="container">
        <QESAlert type="warning" persist={false} classNames="mb-3">Your analysis will overwrite any existing comments and reasons for existing targeted providers in selected projects.</QESAlert>
        <div className="d-flex mb-3">
          <div className="col-6 ps-0 h-100 pe-2">
            <QESCard title="Details" classNames="h-100">
              <label className="qes-select__label">
                Action Type
                <span className="text-danger"> *</span>
              </label>
              <div className="ps-3 mb-3">
                <div className="mb-2">
                  <QESRadioButton
                    label="Append to existing targeted providers"
                    name="target-action-type"
                    id="append-action"
                    value={BulkTargetExistingTargetActionEnum.Append}
                    checked={wizardContext.existingTargetAction === BulkTargetExistingTargetActionEnum.Append}
                    onChange={(e) => wizardContext.setExistingTargetAction((e.target as HTMLInputElement).value as BulkTargetExistingTargetActionEnum)}
                    testId="action-targeting__append"
                  />
                </div>
                <div className="mb-2">
                  <QESRadioButton
                    label="Replace existing targeted providers"
                    name="target-action-type"
                    id="replace-action"
                    value={BulkTargetExistingTargetActionEnum.Replace}
                    checked={wizardContext.existingTargetAction === BulkTargetExistingTargetActionEnum.Replace}
                    onChange={(e) => wizardContext.setExistingTargetAction((e.target as HTMLInputElement).value as BulkTargetExistingTargetActionEnum)}
                    testId="action-targeting__replace"
                  />
                </div>
              </div>
              <QESSelect
                label="Targeting Reason"
                placeholder="Targeting Reason"
                required={true}
                testId="import-targeting__reason"
                value={wizardContext.targetingReason}
                onChange={(e) => wizardContext.setTargetingReason((e.target as HTMLSelectElement).value)}
                isValid={!!wizardContext.targetingReason}
              >
                {targetingReasons}
              </QESSelect>
              <QESTextArea
                label="Comments"
                placeholder="Type here to enter comments..."
                maxLength={100}
                testId="import-targeting__comments"
                required={wizardContext.targetingReason === "Other"}
                value={wizardContext.comment}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => wizardContext.setComment((e.target as HTMLTextAreaElement).value)}
                isValid={!!wizardContext.comment}
              />
            </QESCard>
          </div>
          <div className="col-6 ps-2 pe-0">
            <QESCard title="Report" classNames="h-100">
              <QESTextInput label="Report Name" testId="import-targeting__export-name" value={wizardContext.filename} onChange={(e: ChangeEvent<HTMLInputElement>) => wizardContext.setFilename(e.target.value)} />
              <p className="mb-2">Report Type</p>
              <div className="ps-3 mb-3">
                <div className="mb-2">
                  <QESRadioButton
                    label="CSV File"
                    name="report-type"
                    id="csv-file"
                    value="CSV"
                    checked={wizardContext.formatType === "CSV"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-targeting__format-csv"
                  />
                </div>
                <div className="mb-2">
                  <QESRadioButton
                    label="Pipe-delimited Text"
                    name="report-type"
                    id="pipe-delimited-text"
                    value="Pipe"
                    checked={wizardContext.formatType === "Pipe"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-targeting__format-pipe"
                  />
                </div>
                <div className="mb-2">
                  <QESRadioButton
                    label="Tab-delimited Text"
                    name="report-type"
                    id="tab-delimited-text"
                    value="Tab"
                    checked={wizardContext.formatType === "Tab"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-targeting__format-tab"
                  />
                </div>
              </div>
              <div className="ps-2 mt-2 mb-2">
                <QESCheckbox
                  label="Alert me when the report is ready to view"
                  id="alert-when-ready"
                  checked={wizardContext.emailOnSuccess}
                  onChange={(e) => wizardContext.setEmailOnSuccess((e.target as HTMLInputElement).checked)}
                  testId="import-targeting__email-me"
                />
              </div>
              {isDistinctServicingProviderReportEnabled &&
                <div className="ps-2 mt-2 mb-2">
                  <QESCheckbox
                    label="Distinct Servicing Providers"
                    checked={wizardContext.isDistinctServicingProviderReport}
                    onChange={(e) => handleDistinctProviderReportChange(e.target.checked)}
                    testId="volume-reports__distinct-servicing-provider-report"
                  />
                </div>
              }
              {isReportMetadataTargetTermEnabled &&
                <div className="ps-2 mt-2 mb-2">
                  <QESCheckbox
                    label="Include applied filters in report export"
                    checked={wizardContext.includeReportMetadataSummary}
                    onChange={(e) => wizardContext.setIncludeReportMetadataSummary(e.target.checked)}
                    testId="import-targeting__include-report-metadata-summary-checkbox"
                  />
                </div>
              }
            </QESCard>
          </div>
        </div>
        <div className="mb-2">
          <Waffles<ResultsSummaryTableData>
            columns={reviewColumnConfig}
            data={wizardContext.resultsSummary}
            fullWidth={true}
            testId="import-targeting-results-summary"
          />
        </div>
        <div className="mb-2">
          <Waffles<ReviewProjectsTableData>
            columns={reviewColumnConfig}
            data={wizardContext.reviewProjects}
            fullWidth={true}
            testId="import-targeting-review-projects"
          />
        </div>
        <div className="mb-2">
          {isVolumeTargetingReportConfigurationEnabled &&
            <div className="mb-2">
              <div className="terminations-wizard__card-title">Reports</div>
              <div className="px-3">
                {reportSections.map(section => (
                  <div key={section.key} className="report-item">
                    <ReportCheckbox
                      reportKey={section.key}
                      label={section.title}
                    />
                  </div>
                ))}
              </div>
            </div>
          }
        </div>
      </div>
    </PageSection>
  );
};

export default TargetingWizardConfigureReport;
